// instalacionPwa.ts — captura de `beforeinstallprompt` y disparo de la instalación en Android (round 2026-10-29).
//
// Por qué es un módulo aparte, importado desde `index.tsx` y no un `useEffect` del aviso:
//   Chrome dispara `beforeinstallprompt` UNA vez, en cuanto decide que la página es instalable, y no lo repite. Ese momento
//   puede llegar antes de que React monte (o antes de que la persona inicie sesión y el aviso exista). Si el listener no
//   está registrado desde que arranca el bundle, el evento se pierde y en Android ya no habría cómo ofrecer «Instalar».
//   Por eso `iniciarCapturaInstalacion()` se llama en `index.tsx`, antes de renderizar, y el evento queda guardado acá
//   hasta que alguien (el aviso) lo use. Guard: pwa.guard.test.ts.
//
// Qué se guarda y qué no:
//   - `preventDefault()` en el evento: evita la mini-barra automática de Chrome, para que la invitación salga cuando
//     nosotros decidimos (con la memoria de «Ahora no» y el resto de las reglas) y no encima de cualquier pantalla.
//   - El evento solo sirve para UN `prompt()`. Se suelta en cuanto se usa; si la persona descarta el diálogo del sistema,
//     Chrome puede volver a disparar uno nuevo más adelante y se captura igual.
//   - `appinstalled` (la instalación terminó, por este aviso o por el menú del navegador) apaga el aviso para siempre en
//     este dispositivo.
//
// Sin service worker: Chromium evalúa la instalabilidad con el manifest (nombre, `start_url`, `display`, íconos de 192 y 512
// px, HTTPS) y ya no pide un service worker con `fetch` para ofrecerla; el manifest de esta app lo cumple. A propósito NO se
// registra uno: `serve.json` evita la caché heurística de `index.html` para que una app instalada nunca se quede con un
// bundle viejo, y un service worker con caché lo reintroduciría. Guard: pwa.guard.test.ts.

import { silenciarParaSiempre } from "./memoriaInstalacionPwa";

/** El evento no está en `lib.dom.d.ts`: es de Chromium (Chrome, Edge, Samsung Internet, Opera). */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms?: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform?: string }>;
}

export type ResultadoInstalacion = "accepted" | "dismissed" | "no-disponible";

export interface EstadoInstalacion {
  /** Hay un evento guardado: se puede mostrar «Instalar». */
  disponible: boolean;
  /** `appinstalled` ya ocurrió en esta página. */
  instalada: boolean;
}

/** Lo único que se le pide a `window` (un `EventTarget` cualquiera sirve en las pruebas). */
export interface DestinoEventos {
  addEventListener(tipo: string, oyente: (e: Event) => void): void;
}

let evento: BeforeInstallPromptEvent | null = null;
let instalada = false;
let iniciado = false;
let estado: EstadoInstalacion = { disponible: false, instalada: false };
const oyentes = new Set<() => void>();

function publicar(): void {
  // Objeto NUEVO solo cuando cambia algo: `useSyncExternalStore` compara por referencia.
  if (estado.disponible === (evento !== null) && estado.instalada === instalada) return;
  estado = { disponible: evento !== null, instalada };
  for (const avisar of Array.from(oyentes)) avisar();
}

/**
 * Registra los dos listeners. Es idempotente (StrictMode, recargas en caliente, un segundo import). Se llama UNA vez desde
 * `index.tsx`, lo más arriba posible. El `destino` existe para las pruebas.
 */
export function iniciarCapturaInstalacion(destino: DestinoEventos | null = typeof window === "undefined" ? null : window): void {
  if (iniciado || !destino) return;
  iniciado = true;
  destino.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    evento = e as BeforeInstallPromptEvent;
    publicar();
  });
  destino.addEventListener("appinstalled", () => {
    evento = null;
    instalada = true;
    silenciarParaSiempre("instalada");
    publicar();
  });
}

export function suscribirInstalacion(oyente: () => void): () => void {
  oyentes.add(oyente);
  return () => {
    oyentes.delete(oyente);
  };
}

export function leerEstadoInstalacion(): EstadoInstalacion {
  return estado;
}

/**
 * Muestra el diálogo de instalación del sistema. Debe llamarse DENTRO del clic de la persona (el navegador exige un gesto).
 * Devuelve la elección o `no-disponible` si no hay evento (o si el navegador rechaza el `prompt()`).
 */
export async function solicitarInstalacion(): Promise<ResultadoInstalacion> {
  const e = evento;
  if (!e) return "no-disponible";
  // Un evento solo sirve para un `prompt()`: se suelta antes de esperar, para que un segundo clic no lo repita. Lo que ven
  // los suscriptores (`disponible`) cambia recién al terminar, para que el aviso no desaparezca de golpe mientras el
  // sistema muestra su diálogo.
  evento = null;
  try {
    await e.prompt();
    const eleccion = await e.userChoice;
    return eleccion.outcome === "accepted" ? "accepted" : "dismissed";
  } catch {
    // InvalidStateError (evento ya usado) u otro rechazo: no hay nada que ofrecer.
    return "no-disponible";
  } finally {
    publicar();
  }
}

/** Solo para pruebas: vuelve el módulo a su estado inicial. Los listeners ya registrados en el destino quedan donde estaban. */
export function reiniciarInstalacionParaPruebas(): void {
  evento = null;
  instalada = false;
  iniciado = false;
  estado = { disponible: false, instalada: false };
  oyentes.clear();
}
